import { Injectable } from "@angular/core";
import { UiUser } from "../models/ui-user.model";
import {
  IntakeClinicChoice,
  IntakeDentistChoice,
  IntakeParty,
  IntakePartyOutcome,
  IntakeShilaPoChoice,
  IntakeShilaPoList,
  IntakeShilaPoOutcome,
} from "../models/intake-party.model";
import { ShilaDentistChoice, ShilaPoImport, ShilaPoList } from "../models/shila-po.model";
import { Observable } from "rxjs";
import { ServiceOptions } from "../models/service-options";
import { HttpClient } from "@angular/common/http";
import { AppState } from "../redux/store";
import { SharedService } from "./shared.service";
import { AuthService } from "./auth.service";
import { map, catchError, take } from "rxjs/operators";
import {
  DataElement,
  FileUpload,
  ProcessRunStatusResult,
  ServiceCallInfo,
} from "../models/common";
import { ServiceMetaData } from "../models/service-meta.model";
import {
  OrderSearchParams,
  Order,
  EditableParams,
  OrderLine,
  OrderActivityLog,
  OrderFile,
  SystemParamSubject,
  DueDateChangeAccept,
  OrderNote,
  DrProfile,
  DrProfileSearchParams,
  DrProfilePref,
  RXOpt,
  RXOptValue,
  RXForm,
  RXNote,
  RXLine,
  RXOptGroup,
  RXTooth,
  RXItem,
  RXPdf,
  PresetNoteSearchParams,
  PresetNote,
  CreateOrderData,
  OrderItem,
  OrderEmail,
  OrderEmailContent,
  QCStatusEnum,
  QCStageOpt,
  RXGist,
  IntakeState,
  isFreeRxNote,
} from "../models/order.model";
import { DatePipe } from "@angular/common";
import { bool } from "aws-sdk/clients/signer";
import {
  SupplyOrderSearchParams,
  SupplyOrder,
  SupplyItem,
  SupplyOrderResponse,
} from "../models/supply-order.model";
import {
  DashboardData,
  OrdersSummaryCnt,
  Banner,
  News,
} from "../models/dashboard.model";
import { Clinic, ClinicSearchParams } from "../models/clinic.model";
import { AgentTask, AgentTaskSearchParams } from "../models/agent-tasks.model";
import { param } from "jquery";
import { UntypedFormArray } from "@angular/forms";
import { ConnectionService } from "ng-connection-service";
import { Store } from "@ngrx/store";
import {
  GET_AGENT_TASKS,
  GET_CLINIC_DETAILS,
  GET_DASHBOARD_DATA,
  GET_DR_PROFILES,
  GET_ORDERS,
  GET_PRESET_NOTES,
  GET_RX_ITEMS,
  GET_SUPPLY_ORDERS,
  SELECT_RX_FORM,
} from "../ngrx/ngrx-actions";
import { clinicList, rxItems, rxOpt } from "../ngrx/ngrx-selectors";
import { ResizableColumn } from "primeng/table";
import mime from "mime";

@Injectable({
  providedIn: "root",
})
export class BroshService {
  mockupSw: boolean = true;
  private mockupDataUrl = "assets/mock/";

  clinicList: Clinic[];
  rxOpt: RXOpt[];
  rxItems: RXItem[];

  constructor(
    private readonly store: Store,
    private http: HttpClient,
    private authService: AuthService,
    private datePipe: DatePipe,
    private sharedService: SharedService,
    private connectionService: ConnectionService
  ) {
    this.store.select(clinicList).subscribe((sm) => {
      this.clinicList = sm;
    });
    this.store.select(rxOpt).subscribe((sm) => {
      this.rxOpt = sm;
    });
    this.store.select(rxItems).subscribe((sm) => {
      this.rxItems = sm;
    });
  }

  getOrderHeaderSingle(orderId: number, qcMode: boolean = false, quiet: boolean = false): Observable<Order> {
    let response = new Observable<Order>();
    let serviceName = "get_order_header_single";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["versionId"] = 0;
    params["orderId"] = orderId;
    if (qcMode) params["qcMode"] = true;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    info.alertOnError = !quiet;
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractGetOrders(data, info, true, qcMode)[0]),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  // prevSearchId: this tab's search that this one replaces. The init drops only
  // it, so the user's other tabs and devices keep paging theirs.
  getOrderHeaderInit(
    orderSearchParams: OrderSearchParams,
    quiet: boolean = false,
    prevSearchId: number = null
  ): Observable<ServiceMetaData> {
    let response = new Observable<ServiceMetaData>();
    let serviceName = "get_order_header_init_json";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["versionId"] = 0;
    params["searchType"] = "SEARCH";
    if (prevSearchId) params["prevSearchId"] = prevSearchId;

    if (orderSearchParams.chatMsg)
      params["chatCode"] = orderSearchParams.chatMsg?.id;

    if (orderSearchParams.chatCode)
      params["chatCode"] = orderSearchParams.chatCode;

    if (orderSearchParams.summaryCode)
      params["summaryCode"] = orderSearchParams.summaryCode;

    if (orderSearchParams.orderIdMmd)
      params["orderIdMmd"] = orderSearchParams.orderIdMmd;

    if (orderSearchParams.orderId)
      params["orderIdList"] = orderSearchParams.orderId;

    if (orderSearchParams.clinicList && orderSearchParams.clinicList.length > 0)
      params["clinicIdList"] = orderSearchParams.clinicList
        .map((e) => e.id)
        .join(",");

    if (orderSearchParams.deliverer)
      params["delivererCode"] = orderSearchParams.deliverer?.id;

    if (orderSearchParams.doctorList && orderSearchParams.doctorList.length > 0)
      params["doctorIdList"] = orderSearchParams.doctorList
        .map((e) => e.id)
        .join(",");

    if (orderSearchParams.rxItemList && orderSearchParams.rxItemList.length > 0)
      params["rxItemCodeList"] = orderSearchParams.rxItemList
        .map((e) => e.id)
        .join(",");

    if (orderSearchParams.taskOwnerList
      && orderSearchParams.taskOwnerList.length > 0)
      params["taskOwnerList"] = orderSearchParams.taskOwnerList
        .map((e) => e.userId)
        .join(",");

    if (orderSearchParams.orderFromDate)
      params["orderDateFrom"] = orderSearchParams.orderFromDate.to_DD_MM_YYYY();

    if (orderSearchParams.orderToDate)
      params["orderDateTo"] = orderSearchParams.orderToDate.to_DD_MM_YYYY();

    if (orderSearchParams.patientName)
      params["patientFullName"] = orderSearchParams.patientName;

    if (orderSearchParams.stage) params["stageId"] = orderSearchParams.stage.id;
    if (orderSearchParams.favoriteSw) params["favoriteSw"] = 1;
    if (orderSearchParams.tasksSw) params["chatCode"] = "ALL_OPEN_TASKS";
    if (orderSearchParams.rxSw) params["downloadRxSw"] = 1;
    if (orderSearchParams.inhouseSw) params["inhouseSw"] = true;
    if (orderSearchParams.qcModeSw) params["qcMode"] = true;

    if (orderSearchParams.ordScanNum)
      params["ordScanNum"] = orderSearchParams.ordScanNum;

    if (orderSearchParams.ordTik) params["ordTik"] = orderSearchParams.ordTik;

    if (orderSearchParams.urgency) {
      if (this.authService.currentUser.org == "PLANT") {
        params["urgencyPlantCode"] = orderSearchParams.urgency.id;
      } else {
        params["urgencyLabCode"] = orderSearchParams.urgency.id;
      }
    }

    params.urldecodeSw = 1;
    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    info.alertOnError = !quiet;
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractGetOrderHeaderInit(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractGetOrderHeaderInit(
    res: any,
    info: ServiceCallInfo
  ): ServiceMetaData {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();
    if (data) {
      metaData.totalRecords = data.totalRec;
      metaData.searchId = data.searchId;
    }

    return metaData;
  }

  getOrderHeaderPage(
    searchId: number,
    fromRec: number,
    toRec: number,
    qcMode: boolean = false,
    quiet: boolean = false
  ): Observable<Order[]> {
    let response = new Observable<Order[]>();
    let serviceName = "get_order_header_page_json";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["searchId"] = searchId;
    params["fromRec"] = fromRec;
    params["toRec"] = toRec;
    if (qcMode) params["qcMode"] = true;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    info.alertOnError = !quiet;
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractGetOrders(data, info, false, qcMode)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractGetOrders(
    res: any,
    info: ServiceCallInfo,
    isSingle: boolean = false,
    qcMode: boolean = false
  ): Order[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: Order[] = [];
    let data = res.reply.requestResult.data;

    let metaData = new ServiceMetaData();
    if (data && data.metadata) {
      metaData.totalRecords = data.metadata.totalRecords;
      metaData.fromRecord = data.metadata.fromRecord;
      metaData.toRecord = data.metadata.toRecord;
      metaData.searchId = data.metadata.searchId;
    }

    if (data && data.orders) {
      let ordersTag = data.orders;

      if (ordersTag) {
        let arr = isSingle
          ? new Array().concat(ordersTag.order)
          : new Array().concat(ordersTag);
        arr.forEach((i) => {
          let order = new Order();
          order.barcode = i.barcode;
          order.shade = i.shade;
          if (order.shade) order.shade = order.shade.toUpperCase();
          order.clinicDescr = i.clinicDescr;
          order.clinicId = i.clinicId;
          order.origClinicDescr = i.origClinicDescr;
          order.origClinicId = i.origClinicId;
          order.curDueDate = this.authService.fromXMLDate(i.curDueDate);
          order.doctorFullName = i.doctorFullName;
          order.doctorId = i.doctorId;
          order.tasksSw = this.authService.fromXMLBoolean(i.tasksSw);
          order.favoriteSw = this.authService.fromXMLBoolean(i.favoriteSw);
          order.orderDate = this.authService.fromXMLDate(i.orderDate);
          order.orderId = i.orderId;
          order.orderIdMmd = i.orderIdMmd;
          order.displayId = i.displayId;
          order.orderIdMmd = i.orderIdMmd ?? i.displayId;
          order.orderPdfUrl = i.orderPdfUrl;
          order.orderTypeCode = i.orderTypeCode;
          order.orderTypeDescr = i.orderTypeDescr;
          order.mmdClosedSw = i.mmdClosedSw;
          if (i.patient) {
            order.patientBirthYear = i.patient.birthYear;
            order.patientGender = i.patient.gender;
            order.patientFirstName = i.patient.patientFirstName;
            order.patientId = i.patient.patientId;
            order.patientLastName = i.patient.patientLastName ?? "";
            order.patientTz = i.patient.patientTz;
            order.patientFullName =
              order.patientFirstName + " " + order.patientLastName;
          }
          order.plantDescr = i.plantDescr;
          order.plantId = i.plantId;
          order.plantOrderDate = this.authService.fromXMLDate(i.plantOrderDate);
          order.stageId = i.stageId;
          order.stageDescr = i.stageDescr;
          order.statusCnt = i.statusCnt;
          order.statusCode = i.statusCode;
          order.intake = this.extractIntake(i.intake);
          order.statusDescr = i.statusDescr;
          order.agentCode = i.agentCode;
          order.agentDescr = i.agentDescr;
          order.stageRepeatSw = this.authService.fromXMLBoolean(
            i.stageRepeatSw
          );
          order.totMsgCnt = i.totMsgCnt;
          order.unreadMsgCnt = i.unreadMsgCnt;
          order.unreadSw = this.authService.fromXMLBoolean(i.unreadSw);
          order.plantDueDate = this.authService.fromXMLDate(i.dueDatePlant);
          order.clinicDueDate = this.authService.fromXMLDate(i.dueDateClinic);
          order.urgencyLabCode = i.urgencyLabCode;
          order.urgencyPlantCode = i.urgencyPlantCode;
          order.urgencyPlantDescr = i.urgencyPlantDescr;
          order.urgencyLabDescr = i.urgencyLabDescr;
          order.ordTik = i.ordTik;
          order.ordScanNum = i.ordScanNum;
          order.stageLongDescr = i.stageLongDescr;
          order.totTasksCnt = i.totTasksCnt;
          order.openTasksCnt = i.openTasksCnt;
          order.orderTrackerGroup = i.orderTrackerGroup;
          order.leumitSw = i.leumitSw;
          order.inhouseSw = i.inhouseSw;
          order.refundSw = i.refundSw;
          order.allowCreateRxSw = i.allowCreateRxSw;
          order.qcStatus = i.qcStatus;
          order.qcStatusUserName = i.qcStatusUserName;

          if (order.orderDate)
            order.orderDateFormatted = this.sharedService.formatDate(
              order.orderDate
            );
          if (order.curDueDate)
            order.curDueDateFormatted = this.sharedService.formatDate(
              order.curDueDate
            );
          if (order.clinicDueDate)
            order.clinicDueDateFormatted = this.sharedService.formatDate(
              order.clinicDueDate
            );
          if (order.plantDueDate)
            order.plantDueDateFormatted = this.sharedService.formatDate(
              order.plantDueDate
            );

          order.alertDueDateChangeSw = this.authService.fromXMLBoolean(
            i.alertDueDateChangeSw
          );

          order.origDueDate = this.authService.fromXMLDate(i.origDueDate);

          if (order.origDueDate)
            order.origDueDateFormatted = this.sharedService.formatDate(
              order.origDueDate
            );
          if (this.authService.fromXMLDate(i.origDueDateClinic))
            order.origClinicDueDate = this.sharedService.formatDate(
              i.origDueDateClinic
            );
          if (this.authService.fromXMLDate(i.origDueDatePlant))
            order.origPlantDueDate = this.sharedService.formatDate(
              i.origDueDatePlant
            );

          if (this.authService.currentUser.org == "LAB") {
            order.origDueDateForUser = order.origDueDateFormatted;
          } else if (this.authService.currentUser.org == "CLINIC") {
            order.origDueDateForUser = order.origClinicDueDate;
          } else {
            order.origDueDateForUser = order.origPlantDueDate;
          }

          if (this.authService.fromXMLDate(i.dueDateChangeDate))
            order.dueDateChangeDate = this.sharedService.formatDate(
              i.dueDateChangeDate
            );

          order.dueDateChangeAccept = [];

          if (i.dueDateAccept) {
            let arrAccept = new Array().concat(i.dueDateAccept);
            arrAccept.forEach((a) => {
              var user1 = new DueDateChangeAccept();
              user1.userDescr = a.userDescr;
              let acceptDate = this.authService.fromXMLDate(a.userAcceptDate);
              if (acceptDate) {
                user1.userAcceptDate =
                  this.sharedService.formatDate(acceptDate);
                order.dueDateChangeAccept.push(user1);
              }
            });
          }

          order.files = [];
          if (i.file) {
            let arrFile = new Array().concat(i.file);
            arrFile.forEach((i) => {
              let file = this._extractOrderFile(i);
              if (i.custProdType == "CUST") order.files.push(file);
            });
          }

          order.filesRX = [];
          if (i.rx) {
            let arrFile = new Array().concat(i.rx);
            arrFile.forEach((i) => {
              let file = this._extractOrderFile(i);
              order.filesRX.push(file);
            });
          }

          if (i.orderNotes) {
            let arrNotes = isSingle
              ? new Array().concat(i.orderNotes.row)
              : new Array().concat(i.orderNotes);

            arrNotes.forEach((a) => {
              var note = new OrderNote();
              note.noteId = a.noteId;
              note.text = a.nText;
              note.createUser = a.createUser;
              note.createUserDescr = a.createUserDescr;
              note.timestamp = a.crTime;
              order.notes.push(note);
            });
          }

          if (i.custLine) {
            let arr = new Array().concat(i.custLine);
            arr.forEach((line) => {
              order.orderLines.push(
                this._extractOrderLineTag(line, order.orderLines.length + 1)
              );
            });
          }

          if (i.orderEmails) {
            let arr = new Array().concat(i.orderEmails);
            arr.forEach((a) => {
              var email = new OrderEmail();
              email.status = a.status;
              email.recipients = a.recipients;
              email.crDate = a.createDate;
              email.templateCode = a.templateCode;
              email.templateDescr = a.templateDescr;
              email.subject = a.subject;
              email.body = a.body;
              email.updUserDescr = a.updUserDescr;
              email.updDate = a.updDate;
              email.sendDate = a.sendDate;
              email.delayId = a.delayId;
              email.id = a.id;
              order.orderEmails.push(email);
            });
          }


          if (i.qcStageOptions) {
            let arr = new Array().concat(i.qcStageOptions);
            arr.forEach((a) => {
              let option = new QCStageOpt();
              option.id = a.stageId;
              option.text = a.descr;
              option.enabledSw = a.enabledSw;
              option.primarySw = a.primarySw;
              order.qcStageOptions.push(option);
            });
          }

          if (i.rxGist) {
            let gist = new RXGist();
            gist.lines = this._extractRXLines(i.rxGist);
            gist.rxOptions = this._extractRXOptions(i.rxGist);
            gist.implantTeeth = this._extractRXImplantTeeth(i.rxGist);
            gist.approveDate = this.authService.fromXMLDate(i.rxGist.approveDate);
            order.rxGist = gist;
          }

          order.technician = i.technician;

          result.push(order);
        });
      }
    }

    if (!isSingle) {
      this.store.dispatch(
        GET_ORDERS({
          data: result,
          medataData: metaData,
          qcModeSw: qcMode,
        })
      );
    }

    return result;
  }

  private _extractOrderFile(i: any): OrderFile {
    let file = new OrderFile();
    file.fileId = i.fileId;
    file.fileName = i.fileName;
    if (file.fileName) {
      file.fileName = file.fileName.split("\\").pop().split("/").pop();
    }
    file.fileType = i.fileType;
    if (file.fileType) {
      file.fileType = file.fileType.toLowerCase();
    }
    file.url = i.fileUrl;

    file.mimeType = (mime as any).getType(file.url);
    if (file.mimeType?.split("/")[0] == "video") {
      file.isVideo = true;
      file.videoSrc = file.url;
      // file.videoSrc = [
      //   {
      //     src: file.url,
      //     type: mime.getType(file.url),
      //   },
      // ];
    }

    try {
      file.uploadDate = this.authService.fromXMLDate(i.uploadTimestamp);

      if (file.uploadDate)
        file.uploadDateFormatted = this.sharedService.formatDate(
          file.uploadDate
        );
    } catch (e) { }

    file.modelType = i.modelType;

    file.rxSw = this.authService.fromXMLBoolean(i.rxSw);
    file.lastDownloadTimestamp = i.lastDownloadTimestamp;
    file.lastDownloadUserDescr = i.lastDownloadUserDescr;

    file.rxType = i.type;
    file.formId = i.formId;
    // file.uploadDate = i.updDate;
    // file.uploadDateFormatted = this.sharedService.formatDate(file.uploadDate);
    file.statusCode = i.statusCode;

    return file;
  }

  getOrderLines(order: Order): Observable<OrderLine[]> {
    let response = new Observable<OrderLine[]>();
    let serviceName = "get_order_lines";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["versionId"] = 0;
    params["orderId"] = order.orderId;
    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractOrderLines(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractOrderLines(res: any, info: ServiceCallInfo): OrderLine[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: OrderLine[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.custLine) {
      let linesTag = data.custLine;
      let arr = new Array().concat(linesTag);
      arr.forEach((i) => {
        result.push(this._extractOrderLineTag(i, result.length + 1));
      });
    }

    return result;
  }

  _extractOrderLineTag(i: any, seq: number): OrderLine {
    let line = new OrderLine(seq);
    line.custLineId = i.custLineId;
    line.lineType = i.lineType;
    line.item = new OrderItem(i.odsItem, i.odsItemDesc);
    line.itemCode = line.item?.id;
    line.qty = i.odsQuantity;
    line.teethList = i.teethList;
    line.newLineSw = i.newLineSw;
    line.editableSw = i.editableSw;
    line.lineTotal = i.odsLineTotal;
    line.lineDiscount = i.odsDiscount;
    line.lineUnitPrice = i.odsUnitPrice;
    return line;
  }

  getOrderActivityLog(order: Order): Observable<OrderActivityLog[]> {
    let response = new Observable<OrderActivityLog[]>();
    let serviceName = "get_order_activity_log";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractOrderActivityLog(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractOrderActivityLog(
    res: any,
    info: ServiceCallInfo
  ): OrderActivityLog[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: OrderActivityLog[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.log) {
      let tag = data.log;
      let arr = new Array().concat(tag);
      arr.forEach((i) => {
        let log = new OrderActivityLog();
        log.activityId = i.activityId;
        log.activityType = i.activityType;
        log.activityDisplayText = i.displayText;
        log.stageLongDescr = i.stageLongDescr;
        log.timestamp = this.authService.fromXMLDate(i.timestamp);
        if (log.timestamp) {
          log.timeFormatted = this.datePipe.transform(log.timestamp, "HH:mm");
          log.dateFormatted = this.datePipe.transform(
            log.timestamp,
            "dd/MM/yy"
          );
        }
        log.updUserDescr = i.updUserDescr;
        log.updUserId = i.updUserId;
        log.updUserRole = i.updUserRole;
        log.versionId = i.versionId;
        result.push(log);
      });
    }

    return result;
  }

  uploadCustOrderFile(
    order: Order,
    fileData: FileUpload,
    isRX: boolean
  ): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "upload_cust_order_file";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["fileUuid"] = fileData.fileUuid;
    params["fileName"] = fileData.file.name;
    params["fileType"] = fileData.fileType.replace(".", "");
    params["custProdType"] = isRX ? "PROD" : "CUST";

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateOrderFavorite(order: Order, favoriteSw: boolean): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "update_order_favorite";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["favoriteSw"] = favoriteSw ? 1 : 0;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getSupplyOrders(
    orderSearchParams: SupplyOrderSearchParams
  ): Observable<SupplyOrder[]> {
    let response = new Observable<SupplyOrder[]>();
    let serviceName = "get_supplies_orders";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (orderSearchParams.clinicId)
      params["clinicId"] = orderSearchParams.clinicId;
    if (orderSearchParams.type) params["type"] = orderSearchParams.type;
    if (orderSearchParams.status) params["status"] = orderSearchParams.status;

    if (orderSearchParams.rangeFrom || orderSearchParams.rangeFrom === 0)
      params["fromRec"] = orderSearchParams.rangeFrom;
    if (orderSearchParams.rangeTo) params["toRec"] = orderSearchParams.rangeTo;
    if (orderSearchParams.searchId || orderSearchParams.searchId === 0)
      params["searchId"] = orderSearchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) =>
          this.extractGetSupplyOrders(data, orderSearchParams, info)
        ),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractGetSupplyOrders(
    res: any,
    params: SupplyOrderSearchParams,
    info: ServiceCallInfo
  ): SupplyOrder[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: SupplyOrder[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data && data.order) {
      let ordersTag = data.order;
      metaData.totalRecords = data.totalRec;
      metaData.fromRecord = params.rangeFrom;
      metaData.toRecord = params.rangeTo;
      metaData.searchId = ordersTag.searchId;

      if (ordersTag) {
        let arr = new Array().concat(ordersTag);
        arr.forEach((i) => {
          let order = new SupplyOrder();
          order.approveDate = this.authService.fromXMLDate(i.approveDate);
          order.approveUserName = i.approveUserName;
          order.clinicDescr = i.clinicDescr;
          order.clinicId = i.clinicId;
          order.createDate = this.authService.fromXMLDate(i.createDate);
          order.createDateFormatted = i.createDate;
          order.createUserName = i.createUserName;
          order.orderId = i.orderId;
          order.statusCode = i.statusCode;
          order.statusDescr = i.statusDescr;
          order.notes = i.notes;
          order.city = i.city;
          order.labCreateSw = this.authService.fromXMLBoolean(i.labCreateSw);
          order.deliveryEmail = i.deliveryEmail;
          order.mailSendSw = this.authService.fromXMLBoolean(i.mailSendSw);
          order.mailSentTimestamp = this.authService.fromXMLDate(
            i.mailSentTimestamp
          );
          order.apiSendStatusCode = i.apiSendStatusCode;
          order.apiSendStatusSw = this.authService.fromXMLBoolean(
            i.apiSendStatusSw
          );
          order.apiType = i.apiType;
          order.items = [];

          if (i.items && i.items.item) {
            let arrItems = new Array().concat(i.items.item);
            arrItems.forEach((r) => {
              let item = new SupplyItem();
              item.itemDescr = r.itemDescr;
              item.itemTypeCode = r.itemTypeCode;
              item.qty = r.qty;
              order.items.push(item);
            });

            order.itemListFormatted = order.items
              .map((i) => i.itemDescr)
              .join(" ,");
          }

          result.push(order);
        });
      }
    }

    this.store.dispatch(
      GET_SUPPLY_ORDERS({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  approveSupplyOrder(
    orderId: number,
    publicIdBatchCode?: string
  ): Observable<bool> {
    let response = new Observable<bool>();
    let serviceName = "approve_supplies_order";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    if (publicIdBatchCode) params["publicIdBatchCode"] = publicIdBatchCode;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  createSupplyOrder(
    clinic: DataElement,
    itemTypes: DataElement[],
    notes: string
  ): Observable<SupplyOrderResponse> {
    let response = new Observable<SupplyOrderResponse>();
    let serviceName = "create_supplies_order";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["clinicId"] = clinic.id;
    params["notes"] = notes;
    params["itemTypeList"] = itemTypes.map((i) => i.id).join(",");
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => {
          let rep = data as any;
          if (
            rep.reply.requestStatus.statusCode !== 0 &&
            rep.reply.requestStatus.status == "OPEN_ORDER_EXISTS"
          ) {
            this.sharedService.alertInfo(rep.reply.requestStatus.statusMessage);
            return null;
          } else {
            return this.extractCreateSupplyOrder(data, info);
          }
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractCreateSupplyOrder(
    res: any,
    info: ServiceCallInfo
  ): SupplyOrderResponse {
    this.authService.extractRequestStatusNew(info, res);
    let result = new SupplyOrderResponse();
    let data = res.reply.requestResult.data;
    result.ordersCreated = true;
    result.deliveryOrderId = data["orderId"];
    return result;
  }

  getDashboardData(timer: boolean = false): Observable<DashboardData> {
    let response = new Observable<DashboardData>();
    let serviceName = "get_dashboard_data";
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params["dashboardRefreshType"] = timer ? "TIMER" : "MANUAL";

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    info.alertWhenOffline = false;
    info.online = navigator.onLine;

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractDashboardData(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractDashboardData(res: any, info: ServiceCallInfo): DashboardData {
    this.authService.extractRequestStatusNew(info, res);
    let result = new DashboardData();
    let data = res.reply.requestResult.data;

    if (data && data.ordersSummary) {
      let arr = new Array().concat(data.ordersSummary.row);
      arr.forEach((i) => {
        let row = new OrdersSummaryCnt();
        row.summaryCode = i.summaryCode;
        row.descr = i.descr;
        row.cnt = i.cnt;
        result.ordersSummary.push(row);
      });
    }

    if (data && data.invoicesSummary) {
      result.totalBalDept = data.invoicesSummary.totalBalDept || 0;
    }

    result.pendingPresetNotesCnt = data.pendingPresetNotesCnt;

    if (data && data.chatSummary) {
      result.unreadMsgFromMeCnt = data.chatSummary.unreadFromMeMsgCnt || 0;
      result.unreadMsgByMeCnt = data.chatSummary.unreadByMeMsgCnt || 0;
      result.unreadMsgClinicToMeCnt =
        data.chatSummary.unreadClinicToMeMsgCnt || 0;
      result.unreadMsgPlantToMeCnt =
        data.chatSummary.unreadPlantToMeMsgCnt || 0;
      result.allMsgCnt = data.chatSummary.allMsgCnt || 0;
      result.unreadPlantMgrCnt = data.chatSummary.unreadPlantMgrCnt || 0;
      result.dueDateUpdatesCnt = data.chatSummary.dueDateUpdatesCnt || 0;
    }

    if (data && data.tasksSummary) {
      result.myTasksCnt = data.tasksSummary.myTasksCnt || 0;
      result.allOpenTasksCnt = data.tasksSummary.allOpenTasksCnt || 0;
      result.allClosedTasksCnt = data.tasksSummary.allClosedTasksCnt || 0;
      result.lateTasksCnt = data.tasksSummary.lateTasksCnt || 0;
      result.generalTasksCnt = data.tasksSummary.generalTasksCnt ?? 0;
      result.clinicDelayAlertCnt = data.tasksSummary.clinicDelayAlertCnt ?? 0;
    }

    if (data && data.suppliesSummary) {
      result.openDeliveryOrdersCnt =
        data.suppliesSummary.openDeliveryOrderCnt || 0;
      result.openSuppliesOrdersCnt =
        data.suppliesSummary.openSuppliesOrderCnt || 0;
      result.closedDeliveryOrdersCnt =
        data.suppliesSummary.closedDeliveryOrderCnt || 0;
      result.closedSuppliesOrdersCnt =
        data.suppliesSummary.closedSuppliesOrderCnt || 0;
      result.openDeliveryOrderDate = this.authService.fromXMLDate(
        data.suppliesSummary.openDeliveryOrderDate
      );
      result.openSuppliesOrderDate = this.authService.fromXMLDate(
        data.suppliesSummary.openSuppliesOrderDate
      );
    }

    if (data && data.content) {
      let arr = new Array().concat(data.content.item);
      arr.forEach((i) => {
        if (i.contentType == "BANNER") {
          let c = new Banner();
          c.contentId = i.contentId;
          c.imgUrl = i.imgUrl;
          c.linkUrl = i.linkUrl;
          c.sortOrder = i.sortOrder;
          c.updDate = this.authService.fromXMLDate(i.updDate);
          c.validUntilDate = this.authService.fromXMLDate(i.validUntilDate);
          result.banners.push(c);
        } else if (i.contentType == "NEWS") {
          let c = new News();
          c.contentId = i.contentId;
          c.text = i.text;
          c.sortOrder = i.sortOrder;
          c.updDate = this.authService.fromXMLDate(i.updDate);
          result.news.push(c);
        }
      });
    }

    this.store.dispatch(
      GET_DASHBOARD_DATA({
        data: result,
      })
    );

    return result;
  }

  updateClinicSnooze(
    clinicId: number,
    snoozeSw: boolean,
    snoozeNote: string,
    snoozeEndDate?: Date
  ): Observable<bool> {
    let response = new Observable<bool>();
    let serviceName = "update_clinic_details";
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = 1;

    params["clinicId"] = clinicId;
    params["snoozeSw"] = snoozeSw;
    params["snoozeNote"] = snoozeNote;
    params["snoozeEndDate"] = snoozeEndDate;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateClinicDetails(clinic: Clinic): Observable<bool> {
    let response = new Observable<bool>();
    let serviceName = "update_clinic_details";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["clinicId"] = clinic.id;
    params["deliveryEmail"] = clinic.deliveryEmail;
    params["delayAlertSw"] = clinic.delayAlertSw;
    params["openFridaySw"] = clinic.openFridaySw;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateDashboardBanners(banners: Banner[]): Observable<bool> {
    let response = new Observable<bool>();
    let serviceName = "update_dashboard_content";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["contentType"] = "BANNER";
    params.urldecodeSw = 1;

    const items = [];
    banners.forEach((i, idx) => {
      items.push({
        contentId: i.contentId,
        imgUrl: i.imgUrl,
        sortOrder: idx + 1,
        linkUrl: i.linkUrl,
        validUntilDate: i.validUntilDate?.to_DD_MM_YYYY(),
      });
    });

    params["items"] = {};
    params["items"]["item"] = items;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateDashboardNews(news: News[]): Observable<bool> {
    let response = new Observable<bool>();
    let serviceName = "update_dashboard_content";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["contentType"] = "NEWS";
    params.urldecodeSw = 1;

    const items = [];
    news.forEach((i, idx) => {
      items.push({
        contentId: i.contentId,
        text: i.text,
        sortOrder: idx + 1,
      });
    });

    params["items"] = {};
    params["items"]["item"] = items;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateSystemParams(subject: SystemParamSubject): Observable<bool> {
    let response = new Observable<bool>();
    let serviceName = "update_system_params";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    const paramList = [];
    subject.params.forEach((i) => {
      paramList.push({
        subject: subject.subjectCode,
        code: i.code,
        valueTxt: i.value,
      });
    });

    params["row"] = paramList;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  mailSuppliesOrders(orderId: number): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "brosh.SendPickup";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, true);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getClinicDetails(
    clinicSearchParams: ClinicSearchParams
  ): Observable<Clinic[]> {
    let response = new Observable<Clinic[]>();
    let serviceName = "get_clinic_details";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (clinicSearchParams.clinicId)
      params["clinicId"] = clinicSearchParams.clinicId;

    if (clinicSearchParams.clinic)
      params["clinicId"] = clinicSearchParams.clinic.clinicId;

    if (clinicSearchParams.rangeFrom || clinicSearchParams.rangeFrom === 0)
      params["fromRec"] = clinicSearchParams.rangeFrom;
    if (clinicSearchParams.rangeTo)
      params["toRec"] = clinicSearchParams.rangeTo;
    if (clinicSearchParams.searchId || clinicSearchParams.searchId === 0)
      params["searchId"] = clinicSearchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractGetClinicDetails(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractGetClinicDetails(res: any, info: ServiceCallInfo): Clinic[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: Clinic[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    if (data && data.clinic) {
      let dataTag = data.clinic;
      let metaTag = data.metadata;
      metaData.totalRecords = metaTag.totRec;
      metaData.fromRecord = metaTag.fromRec;
      metaData.toRecord = metaTag.toRec;
      metaData.searchId = metaTag.searchId;

      if (dataTag) {
        let arr = new Array().concat(dataTag);
        arr.forEach((i) => {
          let el = new Clinic(i.clinicId, i.clinicDescr);
          el.deliveryEmail = i.deliveryEmail;
          el.deliveryApiSw = this.authService.fromXMLBoolean(i.deliveryApiSw);
          el.gPlaceId = i.gPlaceId;
          el.gFormattedAddress = i.gFormattedAddress;
          el.gCity = i.gCity;
          el.gStreetNumber = i.gStreetNumber;
          el.gStreetName = i.gStreetName;
          el.cliShort = i.cliShort;
          el.addressNotes = i.addressNotes;
          el.city = i.city;
          el.address = i.address;
          el.openFridaySw = i.openFridaySw;
          el.delayAlertSw = i.delayAlertSw;
          if (i.agentCode) {
            el.agent = new DataElement(i.agentCode, i.agentDescr);
          }
          result.push(el);
        });
      }
    }

    this.store.dispatch(
      GET_CLINIC_DETAILS({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  deleteFile(order: Order, file: OrderFile): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "delete_file";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["fileId"] = file.fileId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  createRXDownloadReq(files: OrderFile[]): Observable<number> {
    let response = new Observable<number>();
    let serviceName = "create_rx_download_req";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["files"] = [];

    files.forEach((f) => {
      params["files"].push({ uuid: f.fileId });
    });

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractCreateRXDownloadReq(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractCreateRXDownloadReq(res: any, info: ServiceCallInfo): number {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    if (data) {
      return data.runNum as number;
    } else {
      return 0;
    }
  }

  getProcessStatus(runNum: number): Observable<ProcessRunStatusResult> {
    let serviceName = "get_process_status";

    let response = new Observable<ProcessRunStatusResult>();
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params["runNum"] = runNum;
    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => {
          return this.extractGetProcessStatus(data, info);
        }),
        catchError((err) => {
          return this.authService.handleErrorNew(err, info);
        })
      );
    return response;
  }

  extractGetProcessStatus(
    res: any,
    info: ServiceCallInfo
  ): ProcessRunStatusResult {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    let result = new ProcessRunStatusResult();

    if (data && data.algRun) {
      result.status = data.algRun.runStatusCode;
      result.errorMsg = data.algRun.runStatusMessage;
      result.outFileUrl = data.algRun.outFileUrl;
    }

    return result;
  }

  acceptDueDateChange(order: Order): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "accept_order_due_date_change";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["prevDueDate"] = order.origDueDate.to_DD_MM_YYYY();
    params["updDueDate"] = order.curDueDate.to_DD_MM_YYYY();

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updOrderNote(
    order: Order,
    note: OrderNote,
    deleteSw: boolean = false
  ): Observable<boolean> {
    let response = new Observable<boolean>();

    let serviceName = "upd_order_note_json";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["nText"] = note.text;
    params["orderIdMmd"] = order.orderIdMmd;
    if (note.noteId) params["noteId"] = note.noteId;
    if (deleteSw) params["deleteSw"] = true;

    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getAgentTasks(searchParams: AgentTaskSearchParams): Observable<AgentTask[]> {
    let response = new Observable<AgentTask[]>();
    let serviceName = "get_agent_tasks";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (searchParams.clinicId) params["clinicId"] = searchParams.clinicId;
    if (searchParams.taskUserId) params["taskUserId"] = searchParams.taskUserId;

    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http.get(this.mockupDataUrl + "get_agent_tasks.json").pipe(
        map((data) => {
          return this.extractAgentTasks(data, searchParams, info);
        })
      );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractAgentTasks(data, searchParams, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractAgentTasks(
    res: any,
    params: AgentTaskSearchParams,
    info: ServiceCallInfo
  ): AgentTask[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: AgentTask[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    let metaTag = data.metadata;
    metaData.totalRecords = metaTag.totalRec;
    metaData.fromRecord = metaTag.rangeFrom;
    metaData.toRecord = metaTag.rangeTo;
    metaData.searchId = metaTag.searchId;

    if (data && data.rows) {
      let rowTag = data.rows;

      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let task = new AgentTask();
          task.taskId = i.taskId;
          if (i.taskUserId) {
            task.taskUser = new UiUser(i.taskUserId, i.taskUserDescr);
          }
          if (i.clinicId) {
            task.clinic = this.clinicList.find((e) => e.id == i.clinicId);
          }

          task.title = i.title;
          task.taskNotes = i.taskNotes;
          task.userNotes = i.taskUserNotes;
          task.updDate = this.authService.fromXMLDate(i.updDate);
          task.dueDate = this.authService.fromXMLDate(i.dueDate);
          task.crDate = this.authService.fromXMLDate(i.crDate);
          task.completeDate = this.authService.fromXMLDate(i.completeDate);
          task.createdBy = new DataElement(i.crBy, i.crUserDescr);
          if (i.updUserId)
            task.updUser = new DataElement(i.updUserId, i.updUserDescr);
          task.status = new DataElement(i.status, i.statusDesc);
          result.push(task);
        });
      }
    }

    this.store.dispatch(
      GET_AGENT_TASKS({
        data: result,
        medataData: metaData,
      })
    );

    return result;
  }

  updAgentTask(task: AgentTask): Observable<boolean> {
    let response = new Observable<boolean>();

    let serviceName = "upd_agent_task";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["taskId"] = task.taskId;
    params["taskUserId"] = task.taskUser?.userId;
    params["clinicId"] = Number.parseInt(task.clinic?.id);
    params["title"] = task.taskId;
    params["dueDate"] = task.dueDate?.to_DD_MM_YYYY();
    params["status"] = task.status?.id;
    params["title"] = task.title;
    params["taskNotes"] = task.taskNotes;
    params["taskUserNotes"] = task.userNotes;
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getDrProfiles(
    searchParams: DrProfileSearchParams,
    dispatch: boolean = true
  ): Observable<DrProfile[]> {
    let response = new Observable<DrProfile[]>();
    let serviceName = "get_dr_profiles";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["clinicId"] = searchParams.clinicId;
    params["doctorId"] = searchParams.doctorId;
    params["profileId"] = searchParams.profileId;

    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http.get(this.mockupDataUrl + "get_dr_profiles.json").pipe(
        map((data) => {
          return this.extractDrProfiles(data, info, dispatch);
        })
      );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractDrProfiles(data, info, dispatch)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractDrProfiles(
    res: any,
    info: ServiceCallInfo,
    dispatch: boolean
  ): DrProfile[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: DrProfile[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    let metaTag = data.metadata;
    metaData.totalRecords = metaTag.totalRec;
    metaData.fromRecord = metaTag.rangeFrom;
    metaData.toRecord = metaTag.rangeTo;
    metaData.searchId = metaTag.searchId;

    if (data && data.rows) {
      let rowTag = data.rows;

      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let p = new DrProfile();
          p.profileId = i.profileId;

          if (i.doctorId) p.doctor = new DataElement(i.doctorId, i.doctorDescr);
          if (i.clinicId) p.clinic = new DataElement(i.clinicId, i.clinicDescr);
          p.activeSw = i.activeSw;
          p.updDate = this.authService.fromXMLDate(i.updDate);
          p.signDataUrl = i.signDataUrl;
          p.signedBy = i.signedBy;
          p.signDate = i.signDate;

          if (i.pref) {
            let prefArr = new Array().concat(i.pref);
            prefArr.forEach((pr) => {
              p.pref.push(this._extractProfilePref(pr));
            });
          }

          result.push(p);
        });
      }
    }

    if (dispatch)
      this.store.dispatch(
        GET_DR_PROFILES({
          data: result,
          medataData: metaData,
        })
      );

    return result;
  }

  updDrProfile(profile: DrProfile): Observable<boolean> {
    let response = new Observable<boolean>();

    let serviceName = "upd_dr_profile";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["profileId"] = profile.profileId;
    params["activeSw"] = profile.activeSw;
    params["signDataUrl"] = profile.signDataUrl;
    params["signedBy"] = profile.signedBy;
    params["pref"] = profile.pref.map((i) => {
      return {
        prefId: i.prefId,
        prefType: i.prefType,
        optCode: i.opt?.id,
        optValueCode: i.optValue?.id,
        technicalIssueCode: i.techIssue?.id,
        noteLineType: i.noteLineType,
        note: i.note,
        presetNoteId: i.presetNoteId,
        technicianId: i.technician?.id,
        pgSw: i.pgSw,
        pgSnoozeDate: i.pgSnoozeDate,
      };
    });

    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  _extractProfilePref(pr: any): DrProfilePref {
    let pref = new DrProfilePref();
    pref.prefId = pr.prefId;
    pref.prefType = pr.prefType;
    pref.noteLineType = pr.noteLineType;
    pref.note = pr.note;
    pref.presetNoteId = pr.presetNoteId;
    pref.pgSw = pr.pgSw;
    pref.pgSnoozeDate = this.authService.fromXMLDate(pr.pgSnoozeDate);
    pref.iconKey = pr.iconKey;

    if (pr.technicianId) {
      pref.technician = new DataElement(pr.technicianId, pr.technicianName);
    }

    if (pr.optCode) {
      pref.opt = this.rxOpt.find((r) => r.id == pr.optCode);
      if (pref.opt) {
        pref.optValue = pref.opt.valueList.find((r) => r.id == pr.optValueCode);
        pref.opt.valueNum = pr.optValueNumeric;
        pref.opt.valueText = pr.optValueText;
        pref.opt.valueBool = pr.optValueBool;
      } else {
        pref.opt = new RXOpt(pr.optCode, pr.optDescr);
        if (pr.optValueCode)
          pref.optValue = new RXOptValue(pr.optValueCode, pr.optValueDescr);
      }
    }

    if (pr.technicalIssueCode) {
      pref.techIssue = new DataElement(
        pr.technicalIssueCode,
        pr.technicalIssueDescr
      );
    }

    return pref;
  }

  getOrderDrProfile(orderId: number): Observable<DrProfile> {
    let response = new Observable<DrProfile>();
    let serviceName = "get_order_dr_profile";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http
        .get(this.mockupDataUrl + "get_order_dr_profile.json")
        .pipe(
          map((data) => {
            return this.extractOrderDrProfile(data, info);
          })
        );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractOrderDrProfile(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractOrderDrProfile(res: any, info: ServiceCallInfo): DrProfile {
    this.authService.extractRequestStatusNew(info, res);
    let data = res.reply.requestResult.data;
    let result = new DrProfile();

    if (data) {
      if (data.pref) {
        let rowTag = data.pref;
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          result.pref.push(this._extractProfilePref(i));
        });
      }
      return result;
    } else {
      return null;
    }
  }

  createDrProfile(clinicId: string, doctorId: string): Observable<number> {
    let response = new Observable<number>();

    let serviceName = "create_dr_profile";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    if (clinicId) params["clinicId"] = Number.parseInt(clinicId);
    if (doctorId) params["doctorId"] = Number.parseInt(doctorId);

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          return data.profileId;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getRXItems(): Observable<RXItem[]> {
    let response = new Observable<RXItem[]>();
    let serviceName = "get_rx_items";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractRXItems(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractRXItems(res: any, info: ServiceCallInfo): RXItem[] {
    this.authService.extractRequestStatusNew(info, res);
    let results = [];
    let data = res.reply.requestResult.data;
    if (data.items) {
      let arr = new Array().concat(data.items);
      arr.forEach((i) => {
        let item = new RXItem(i.rxItem, i.itemDescr);
        item.allowSingleSw = i.allowSingleSw;
        item.allowSplintedSw = i.allowSplintedSw;
        item.allowJawSw = i.allowJawSw;
        item.teethQtyType = i.teethQtyType;
        item.displayGroup = i.displayGroup;
        item.lineType = i.lineType;
        item.activeSw = i.activeSw;
        if (i.stages)
          item.stages = i.stages.map((s) => new DataElement(s.code, s.descr));
        if (i.mmdCodes) {
          item.mmdCodes = i.mmdCodes;
          item.mmdCodesFilter = item.mmdCodes.join(",");
        }
        results.push(item);
      });
    }

    this.store.dispatch(
      GET_RX_ITEMS({
        data: results,
      })
    );

    return results;
  }

  getRXForm(orderId: number, formId: number): Observable<RXForm> {
    let response = new Observable<RXForm>();
    let serviceName = "get_rx_form";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    params["formId"] = formId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http.get(this.mockupDataUrl + "get_rx_form.json").pipe(
        map((data) => {
          return this.extractRXForm(data, info);
        })
      );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => {
          return this.extractRXForm(data, info);
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private _extractRXLines(data: any): RXLine[] {
    let lines: RXLine[] = [];
    if (data.lines) {
      let arrLines = new Array().concat(data.lines);
      arrLines.forEach((i) => {
        let line = new RXLine();
        line.lineId = i.lineId;
        line.rxItem = this.rxItems.find((r) => r.id == i.rxItem);

        if (!line.rxItem) {
          return;
        }
        line.qty = i.qty;
        line.stage = new DataElement(i.stageCode, i.stageDescr);
        line.toothDisplayText = i.toothDisplayText;

        if (i.teeth) {
          let arrTeeth = new Array().concat(i.teeth);
          arrTeeth.forEach((t) => {
            let tooth = line.teeth.find((e) => e.id == t.id);
            if (tooth) {
              tooth.type = t.type;
              tooth.splintedGroup = t.splintedGroup;
            }
          });
        }
        lines.push(line);
      });
    }
    return lines;
  }

  private _extractRXImplantTeeth(data: any): RXTooth[] {
    let implantTeeth: RXTooth[] = [];
    if (data.rxTooth) {
      let arrTeeth = new Array().concat(data.rxTooth);
      arrTeeth.forEach((t) => {
        if (t.implantSw) {
          let tooth = Object.assign(
            {},
            SharedService.teethDef.find((e) => e.id == t.id)
          );
          tooth.toothSeq = t.toothSeq;

          this.setupImplantToothOpt(tooth);
          if (t.implantCompany) {
            tooth.implantCompany.selectedValue =
              tooth.implantCompany.valueList.find(
                (r) => r.id == t.implantCompany
              );
          }
          if (t.implantType) {
            tooth.implantType.selectedValue =
              tooth.implantType.valueList.find((r) => r.id == t.implantType);
          }
          if (t.implantDiameter) {
            tooth.implantDiameter.selectedValue =
              tooth.implantDiameter.valueList.find(
                (r) => r.id == t.implantDiameter
              );
          }
          tooth.implantIndivSw.valueBool = t.implantIndivSw;
          tooth.implantMultiUnitSw.valueBool = t.implantMultiUnitSw;
          implantTeeth.push(tooth);
        }
      });
    }
    return implantTeeth;
  }

  private _extractRXOptions(data: any): RXOptGroup[] {
    let rxOptions: RXOptGroup[] = [];
    if (data.rxOpt) {
      let arrRxGroup = new Array().concat(data.rxOpt);
      arrRxGroup.forEach((i) => {
        let group = new RXOptGroup();
        group.optGroup = i.optGroup;
        group.optGroupDescr = i.optGroupDescr;

        let arrOpt = new Array().concat(i.options);
        arrOpt.forEach((o) => {
          let opt = this.rxOpt.find((r) => r.id == o.optCode);
          if (opt.valueType == "LIST" && o.optValueCode) {
            opt.selectedValue = opt.valueList.find(
              (r) => r.id == o.optValueCode
            );
          } else {
            opt.valueNum = o.optValueNumeric;
            opt.valueText = o.optValueText;
            opt.valueBool = o.optValueBool;
          }

          if (
            opt.id == "PUBLIC_ID" ||
            opt.id == "SCAN_ID" ||
            opt.id == "SCANNER"
          ) {
            opt.required = true;
          }
          group.options.push(opt);
        });

        rxOptions.push(group);
      });
    }
    return rxOptions;
  }

  private extractRXForm(res: any, info: ServiceCallInfo): RXForm {
    this.authService.extractRequestStatusNew(info, res);
    let form = new RXForm();
    let data = res.reply.requestResult.data;
    if (data) {
      form.formId = data.formId;
      form.updDate = data.updDate;
      form.crDate = data.crDate;
      form.updUser = data.updUser;
      form.intake = this.extractIntake(data.intake);
      form.statusCode = data.statusCode;
      form.statusDescr = data.statusDescr;
      form.canvasDataUrl = data.canvasDataUrl;
      form.allowChangeToDraft = data.allowChangeToDraft;
      form.mmdSyncPending = !!data.mmdSyncPending;
      form.pdfAfterMmd = data.pdfAfterMmd ?? undefined;
      form.type = data.type;
      form.publicId = data.publicId;
      form.patientId = data.patientId;
      form.patientDescr = data.patientDescr;
      form.rxDefaultLang = data.rxDefaultLang;
      form.showOrderLines = data.showOrderLines;

      if (data.custLine) {
        let arr = new Array().concat(data.custLine);
        arr.forEach((i) => {
          form.orderLines.push(
            this._extractOrderLineTag(i, form.orderLines.length + 1)
          );
        });
      }

      if (data.doctorId) {
        form.doctor = new DataElement(data.doctorId, data.doctorDescr);
      }

      if (data.clinicId) {
        form.clinic = new Clinic(data.clinicId, data.clinicDescr);
      }

      if (data.pdf) {
        let arrPdf = new Array().concat(data.pdf);
        arrPdf.forEach((i) => {
          let pdf = new RXPdf();
          pdf.lang = i.lang;
          pdf.fileUrl = i.fileUrl;
          form.pdf.push(pdf);
        });
      }

      if (data.notes) {
        let arrNotes = new Array().concat(data.notes);
        arrNotes.forEach((i) => {
          let note = new RXNote();
          note.noteId = i.noteId;
          note.drProfileId = i.drProfileId;
          note.presetNoteId = i.presetNoteId;
          note.text = i.text;
          note.kind = i.kind ?? null;
          form.notes.push(note);
        });
      }

      if (!form.notes.find(isFreeRxNote)) {
        let note = new RXNote();
        note.text = "";
        form.notes.push(note);
      }

      form.lines = this._extractRXLines(data);

      if (data.rxTooth) {
        let arrTeeth = new Array().concat(data.rxTooth);
        arrTeeth.forEach((t) => {
          let tooth = Object.assign(
            {},
            SharedService.teethDef.find((e) => e.id == t.id)
          );
          tooth.toothSeq = t.toothSeq;

          if (t.extractSw) {
            form.extractTeeth.push(tooth);
          }
        });
      }

      form.implantTeeth = this._extractRXImplantTeeth(data);
      form.rxOptions = this._extractRXOptions(data);

      if (data.signErrors) {
        let arrErr = new Array().concat(data.signErrors);
        arrErr.forEach((i) => {
          form.validationErrors.push(i.msg);
        });
      }
    }

    return form;
  }

  setupImplantToothOpt(tooth: RXTooth) {
    let opt = this.rxOpt?.find((r) => r.id == "IMP_COMPANY");
    tooth.implantCompany = structuredClone(opt);

    opt = this.rxOpt?.find((r) => r.id == "IMP_TYPE");
    tooth.implantType = structuredClone(opt);

    opt = this.rxOpt?.find((r) => r.id == "IMP_DIAMETER");
    tooth.implantDiameter = structuredClone(opt);

    opt = this.rxOpt?.find((r) => r.id == "IMP_INDIV_ABUT");
    tooth.implantIndivSw = structuredClone(opt);

    opt = this.rxOpt?.find((r) => r.id == "IMP_MULTI_UNIT");
    tooth.implantMultiUnitSw = structuredClone(opt);
  }

  // null: refused because the form's Meimad sync is pending (MMD_SYNC_PENDING), its message already shown
  updRXForm(
    orderId: number,
    form: RXForm,
    updStatus: string = null
  ): Observable<RXForm | null> {
    let response = new Observable<RXForm>();

    let serviceName = "upd_rx_form";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["formId"] = form.formId;
    params["orderId"] = orderId;
    params["statusCode"] = form.statusCode;
    params["teethCrossDataUrl"] = form.teethDataUrl;
    params["canvasDataUrl"] = form.canvasDataUrl;
    if (updStatus == "DELETE") {
      params["deleteSw"] = true;
    } else {
      params["updStatus"] = updStatus;
    }
    params["crDate"] = form.crDate;
    params["patientId"] = form.patientId;
    params["patientDescr"] = form.patientDescr;
    params["doctorId"] = form.doctor?.id;
    params["publicId"] = form.publicId;
    params["clinicId"] = form.clinic?.id;

    params["notes"] = form.notes
      .filter((i) => i.text)
      .map((i) => {
        return {
          noteId: i.noteId,
          drProfileId: i.drProfileId,
          presetNoteId: i.presetNoteId,
          text: i.text,
          kind: i.kind ?? null,
        };
      });

    params["lines"] = form.lines.map((i) => {
      return {
        lineId: i.lineId,
        rxItem: i.rxItem?.id,
        stageCode: i.stage?.id,
        newSw: i.dirtySw,
        qty: i.qty,
        teeth: i.teeth
          .filter((t) => t.type)
          .map((t) => {
            return {
              id: t.id,
              type: t.type,
              splintedGroup: t.splintedGroup,
            };
          }),
      };
    });

    params["rxTooth"] = [];

    form.extractTeeth.forEach((t) => {
      params["rxTooth"].push({
        id: t.id,
        extractSw: true,
        toothSeq: t.toothSeq,
      });
    });

    form.implantTeeth.forEach((t) => {
      params["rxTooth"].push({
        id: t.id,
        implantSw: true,
        implantType: t.implantType?.selectedValue?.id,
        implantCompany: t.implantCompany?.selectedValue?.id,
        implantDiameter: t.implantDiameter?.selectedValue?.id,
        implantIndivSw:
          t.implantType?.selectedValue?.id == "CEMENT"
            ? t.implantIndivSw?.valueBool
            : false,
        implantMultiUnitSw:
          t.implantType?.selectedValue?.id == "SCREW"
            ? t.implantMultiUnitSw?.valueBool
            : false,
      });
    });

    params["rxOpt"] = [];
    form.rxOptions.forEach((g) => {
      g.options.forEach((i) => {
        params["rxOpt"].push({
          optCode: i.id,
          optValueCode: i.selectedValue?.id,
          optValueNumeric: i.valueNum,
          optValueBool: i.valueBool,
          optValueText:
            i.valueText ?? (i.valueType == "LIST" ? i.selectedValue : null),
        });
      });
    });

    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data: any) => {
          // an expected refusal, told as such (not as an error): the caller reloads the form
          if (data.reply?.requestStatus?.status == "MMD_SYNC_PENDING") {
            this.sharedService.alertInfo(data.reply.requestStatus.statusMessage);
            return null;
          }
          if (updStatus != "DELETE") {
            let form = this.extractRXForm(data, info);

            this.store.dispatch(
              SELECT_RX_FORM({
                form: form,
              })
            );

            return form;
          }
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  createRXForm(orderId: number, clinicSw: boolean): Observable<number> {
    let response = new Observable<number>();

    let serviceName = "create_rx_form";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    params["clinicSw"] = clinicSw;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          // return this.extractRXForm(res, info);
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          return data.formId;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  createRXPdf(form: RXForm, teethDataUrl: string): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "brosh.GetRxPdf";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["formId"] = form.formId;
    params["teethCrossDataUrl"] = teethDataUrl;
    params["canvasDataUrl"] = form.canvasDataUrl;
    params["imageFormat"] = "svg";
    params["rxType"] = form.type;
    params["rxDefaultLang"] = form.rxDefaultLang;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, false);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getPresetNotes(
    searchParams: PresetNoteSearchParams
  ): Observable<PresetNote[]> {
    let response = new Observable<PresetNote[]>();
    let serviceName = "get_preset_notes_bank";
    let params = new ServiceOptions();
    params.serviceName = serviceName;
    params.urldecodeSw = 1;

    params["noteText"] = searchParams.noteText;
    if (searchParams.rangeFrom || searchParams.rangeFrom === 0)
      params["fromRec"] = searchParams.rangeFrom;
    if (searchParams.rangeTo) params["toRec"] = searchParams.rangeTo;
    if (searchParams.searchId || searchParams.searchId === 0)
      params["searchId"] = searchParams.searchId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    if (false)
      return this.http
        .get(this.mockupDataUrl + "get_preset_notes_bank.json")
        .pipe(
          map((data) => {
            return this.extractPresetNotes(data, info);
          })
        );

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractPresetNotes(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractPresetNotes(res: any, info: ServiceCallInfo): PresetNote[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: PresetNote[] = [];
    let data = res.reply.requestResult.data;
    let metaData = new ServiceMetaData();

    let metaTag = data.metadata;
    metaData.totalRecords = metaTag.totalRec;
    metaData.fromRecord = metaTag.rangeFrom;
    metaData.toRecord = metaTag.rangeTo;
    metaData.searchId = metaTag.searchId;

    if (data && data.rows) {
      let rowTag = data.rows;

      if (rowTag) {
        let arr = new Array().concat(rowTag);
        arr.forEach((i) => {
          let note = new PresetNote();
          note.id = i.noteId;
          note.updUser = i.updUser;
          note.updDate = this.authService.fromXMLDate(i.updDate);
          note.status = i.status;
          note.statusDescr = i.statusDescr;
          note.allDoctorsSw = i.allDoctorsSw;
          note.displayLabSw = i.displayLabSw;
          note.allItemsSw = i.allItemsSw;

          if (i.rxItems) {
            let arrItems = new Array().concat(i.rxItems);
            arrItems.forEach((it) => {
              note.itemCodes.push(it.rxItem);
            });
          }

          if (i.doctors) {
            let arrDoctors = new Array().concat(i.doctors);
            arrDoctors.forEach((it) => {
              note.doctorCodes.push(it.doctorId);
            });
          }

          let texts: Map<string, string> = new Map();
          let arrText = new Array().concat(i.noteText);
          arrText.forEach((t) => {
            texts.set(t.lang, t.text);
          });

          note.he = texts.get("he") ?? "";
          note.en = texts.get("en") ?? "";
          note.cn = texts.get("cn") ?? "";

          result.push(note);
        });
      }
    }

    this.store.dispatch(
      GET_PRESET_NOTES({
        data: result,
        medataData: metaData,
      })
    );
    return result;
  }

  updPresentNote(note: PresetNote, status: string): Observable<boolean> {
    let response = new Observable<boolean>();

    let serviceName = "upd_preset_note";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["noteId"] = note.id;
    params["allDoctorsSw"] = note.allDoctorsSw;
    params["displayLabSw"] = note.displayLabSw;
    params["allItemsSw"] = note.allItemsSw;
    params["status"] = status;
    params["rxItems"] = note.rxItems.map((i) => i.id);
    params["doctors"] = note.doctors.map((i) => i.id);
    params["noteText"] = [
      {
        lang: "he",
        text: note.he,
      },
      {
        lang: "en",
        text: note.en,
      },
      {
        lang: "cn",
        text: note.cn,
      },
    ];
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  createOrder(clinicId: number): Observable<CreateOrderData> {
    let response = new Observable<CreateOrderData>();

    let serviceName = "create_order";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["clinicId"] = clinicId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          let result = new CreateOrderData();
          result.formId = data.formId;
          result.orderId = data.orderId;
          return result;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  cancelOrder(orderId: number): Observable<CreateOrderData> {
    let response = new Observable<CreateOrderData>();

    let serviceName = "cancel_order";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          let result = new CreateOrderData();
          result.formId = data.formId;
          result.orderId = data.orderId;
          return result;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  approveRx(formId: number): Observable<number> {
    let response = new Observable<number>();
    let serviceName = "approve_rx";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["formId"] = formId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, false);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          return data.labFormId;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  cancelDuplicateOrder(
    formId: number, newOrderIdMmd: Number
  ): Observable<number> {
    let response = new Observable<number>();
    let serviceName = "cancel_duplicate_order";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["formId"] = formId;
    params["newOrderIdMmd"] = newOrderIdMmd;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, false);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          return data.newOrderId;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getShilaPos(orderId: number, search: string = ''): Observable<ShilaPoList> {
    const serviceName = 'get_shila_pos';
    const params = new ServiceOptions();
    params.serviceName = serviceName;
    params['orderId'] = orderId;
    params['search'] = search;
    const url = this.authService.getRequestUrl(serviceName);
    const body = this.authService.getRequestBody(params);
    const info = new ServiceCallInfo(serviceName, url, body);
    return this.http
      .post(url, body, { headers: this.authService.getRequestHeaders(), withCredentials: false, responseType: 'json' })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          const data = res.reply.requestResult.data || {};
          return { eligible: !!data.eligible, reason: data.reason, pos: data.pos || [], imported: data.imported || [], dentist: data.dentist ?? null };
        }),
        catchError((error) => this.authService.handleErrorNew(error, info))
      );
  }

  // jsonb_strip_nulls drops a null intake and a free case's taker keys
  private extractIntake(intake: any): IntakeState | null {
    if (!intake) return null;
    return {
      formId: intake.formId,
      takerUserId: intake.takerUserId ?? null,
      takerName: intake.takerName ?? null,
      party: intake.party ?? null,
    };
  }

  private postIntakeService(serviceName: string, fields: { [key: string]: any }): Observable<any> {
    const params = new ServiceOptions();
    params.serviceName = serviceName;
    Object.keys(fields).forEach((k) => (params[k] = fields[k]));
    const url = this.authService.getRequestUrl(serviceName);
    const body = this.authService.getRequestBody(params);
    const info = new ServiceCallInfo(serviceName, url, body);
    return this.http
      .post(url, body, { headers: this.authService.getRequestHeaders(), withCredentials: false, responseType: 'json' })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          return res.reply.requestResult.data || {};
        }),
        catchError((error) => this.authService.handleErrorNew(error, info))
      );
  }

  takeIntakeCase(
    orderId: number
  ): Observable<{ formId: number; mine: boolean; takerUserId: number | null; takerName: string | null }> {
    return this.postIntakeService('take_intake_case', { orderId }).pipe(
      map((d: any) => ({
        formId: d.formId,
        mine: !!d.mine,
        takerUserId: d.takerUserId ?? null,
        takerName: d.takerName ?? null,
      }))
    );
  }

  giveBackIntakeCase(orderId: number): Observable<boolean> {
    return this.postIntakeService('give_back_intake_case', { orderId }).pipe(map(() => true));
  }

  lockRxForm(
    formId: number,
    activity: boolean,
    loadedUpdDate: string | null
  ): Observable<{ editable: boolean; lockedBy: string | null; stale: boolean; idle: boolean }> {
    return this.postIntakeService('lock_rx_form', { formId, activity, loadedUpdDate }).pipe(
      map((d: any) => ({ editable: !!d.editable, lockedBy: d.lockedBy ?? null, stale: !!d.stale, idle: !!d.idle }))
    );
  }

  unlockRxForm(formId: number): Observable<boolean> {
    return this.postIntakeService('unlock_rx_form', { formId }).pipe(map(() => true));
  }

  getIntakeParty(orderId: number): Observable<IntakeParty> {
    return this.postIntakeService('get_intake_party', { orderId }).pipe(
      map(
        (data: any) =>
          ({
            ...data,
            pendingClinic: data.pendingClinic ?? null,
            clinics: data.clinics || [],
            dentists: data.dentists || [],
            duplicates: data.duplicates || [],
          }) as IntakeParty
      )
    );
  }

  getIntakeShilaPos(orderId: number): Observable<IntakeShilaPoList> {
    return this.postIntakeService('get_intake_shila_pos', { orderId }).pipe(
      map((data: any) => ({ pos: data.pos || [], shilaClinics: data.shilaClinics || [] }))
    );
  }

  setIntakeClinic(orderId: number, choice: IntakeClinicChoice): Observable<IntakePartyOutcome> {
    return this.postIntakeService('set_intake_clinic', { orderId, ...choice }).pipe(map((data: any) => ({ outcome: data.outcome })));
  }

  setIntakeDentist(orderId: number, choice: IntakeDentistChoice): Observable<IntakePartyOutcome> {
    return this.postIntakeService('set_intake_dentist', { orderId, ...choice }).pipe(map((data: any) => ({ outcome: data.outcome })));
  }

  takeIntakeShilaPo(orderId: number, choice: IntakeShilaPoChoice): Observable<IntakeShilaPoOutcome> {
    return this.postIntakeService('take_intake_shila_po', { orderId, ...choice }).pipe(
      map((data: any) => ({ outcome: data.outcome, dentist: data.dentist ?? null }))
    );
  }

  importShilaPos(orderId: number, poNumbers: string[]): Observable<ShilaPoImport> {
    return this.postShilaPos('import_shila_pos', { orderId, poNumbers }).pipe(
      map((data: any) => ({
        imported: data.imported || [],
        doctorMatched: !!data.doctorMatched,
        dentist: data.dentist ?? null,
      }))
    );
  }

  resolveShilaDentist(
    orderId: number,
    choice: ShilaDentistChoice
  ): Observable<{ outcome: 'MATCHED' | 'DECIDED' | 'WAITING' }> {
    return this.postShilaPos('resolve_shila_dentist', { orderId, ...choice }).pipe(map((data: any) => ({ outcome: data.outcome })));
  }

  releaseShilaPos(orderId: number, poNumbers: string[]): Observable<boolean> {
    return this.postShilaPos('release_shila_pos', { orderId, poNumbers }).pipe(map(() => true));
  }

  private postShilaPos(serviceName: string, fields: { [key: string]: any }): Observable<any> {
    const params = new ServiceOptions();
    params.serviceName = serviceName;
    Object.keys(fields).forEach((k) => (params[k] = fields[k]));
    const url = this.authService.getRequestUrl(serviceName);
    const body = this.authService.getRequestBody(params);
    const info = new ServiceCallInfo(serviceName, url, body, false);
    return this.http
      .post(url, body, { headers: this.authService.getRequestHeaders(), withCredentials: false, responseType: 'json' })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          return res.reply.requestResult.data || {};
        }),
        catchError((error) => this.authService.handleErrorNew(error, info))
      );
  }

  getItemCatalog(orderId: number): Observable<OrderItem[]> {
    let response = new Observable<OrderItem[]>();
    let serviceName = "get_item_catalog";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);

    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractItemCatalog(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractItemCatalog(res: any, info: ServiceCallInfo): OrderItem[] {
    this.authService.extractRequestStatusNew(info, res);
    let results = [];
    let data = res.reply.requestResult.data;
    if (data.items) {
      let arr = new Array().concat(data.items);
      arr.forEach((i) => {
        let item = new OrderItem(i.item, i.descr);
        results.push(item);
      });
    }
    return results;
  }

  updOrderLines(orderId: number, lines: OrderLine[], stageId?: string): Observable<boolean> {
    let response = new Observable<boolean>();

    let serviceName = "update_order_lines";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    if (stageId) params["stageId"] = stageId;
    params["lines"] = lines.map((i) => {
      return {
        custLineId: i.custLineId,
        qty: i.qty,
        item: i.item?.id,
        teethList: i.teethList,
        checkedSw: i.selected,
      };
    });

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  sendOrderToMMD(orderId: number): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "brosh.SendOrderToMmd";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, false);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateOrderHeader(
    orderId: number,
    clinicId: string,
    doctorId: number,
    patientFirstName: string,
    orderDate: Date,
    curDueDate: Date,
    ordScanNum: string,
    shade: string,
    stageId: number,
    qcModeSw: boolean
  ): Observable<Order> {
    let response = new Observable<Order>();

    let serviceName = "update_order_header";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    params["clinicId"] = Number.parseInt(clinicId);
    params["doctorId"] = doctorId;
    params["patientFirstName"] = patientFirstName;
    params["orderDate"] = orderDate?.to_DD_MM_YYYY();
    params["curDueDate"] = curDueDate?.to_DD_MM_YYYY();
    params["ordScanNum"] = ordScanNum;
    params["shade"] = shade;
    params["stageId"] = stageId;
    params["qcMode"] = qcModeSw;

    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data: any) => {
          return this.extractGetOrders(data, info, true, true)[0];
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }


  updateOrderQCStatus(
    orderId: number,
    qcStatus: QCStatusEnum
  ): Observable<Order> {
    let response = new Observable<Order>();

    let serviceName = "update_qc_status";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    params["qcStatus"] = qcStatus;


    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data: any) => {
          return this.extractGetOrders(data, info, true, true)[0];
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }


  generateOrderEmailTemplate(
    orderId: number,
    templateCode: string
  ): Observable<OrderEmailContent> {
    let response = new Observable<OrderEmailContent>();

    let serviceName = "generate_order_email_template ";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    params["templateCode"] = templateCode;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((res: any) => {
          this.authService.extractRequestStatusNew(info, res);
          let data = res.reply.requestResult.data;
          let result = new OrderEmailContent();
          result.body = data.body;
          result.subject = data.subject;
          result.recipients = data.recipients;
          return result;
        }),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updateOrderEmail(
    orderId: number,
    email: OrderEmail,
    updStatus: string,
    templateCode: string,
    updUserDescr: string
  ): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "brosh.UpdateOrderEmail";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = orderId;
    params["orderEmailId"] = email.id;
    params["subject"] = email.subject;
    params["body"] = email.body;
    params["recipients"] = email.recipients;
    params["updStatus"] = updStatus;
    params["templateCode"] = templateCode;
    params["userDescr"] = updUserDescr;

    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, false);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }
}
